import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import mascot from "@/assets/img/icon_aimoge.webp";
import { findMixLineupEntry } from "@/config/mixLineup";
import {
  isRunningAsInstalledPwa,
  type PwaInstallPromptState,
  type PwaInstallPromptStore,
  pwaInstallPromptStore,
} from "@/features/mix-onboarding/lib/pwaInstallPrompt";
import { BoardSelectionCards } from "@/features/settings/components/sections/BoardSelectionCards";
import {
  resolveEnabledMixSlugs,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { Button, Toggle } from "@/shared/ui/form";
import { getDialogAccessibilityProps } from "@/shared/ui/overlay/dialogAccessibility";
import { useDialogFocusTrap } from "@/shared/ui/overlay/dialogFocus";

interface DockingOnboardingWallProps {
  registeredSlugs: readonly string[];
  /** 初回案内をすべて終えたときに呼ぶ。永続化と非表示化は呼び出し元の責務。 */
  onComplete: (isAdult: boolean) => void;
  /** テスト用の注入点。 */
  installPromptStore?: PwaInstallPromptStore;
  isInstalledPwa?: () => boolean;
  isMobileDisplay: boolean;
  /** 成人板のスレへ直接入った場合は、板選択にかかわらず年齢確認する。 */
  requiresAdultConfirmation?: boolean;
}

type OnboardingStep = "age" | "boards" | "install";

function useInstallPromptState(
  store: PwaInstallPromptStore,
): PwaInstallPromptState {
  return useSyncExternalStore(store.subscribe, store.getState, store.getState);
}

/**
 * 総合カタログ初回パネル。R-18板だけの構成では年齢確認を先に行い、
 * 非R18板もある構成では板選択後、R-18板を選んだ場合だけ年齢確認する。
 * 完了までは不透明のウォールとして描画し、閉じる手段は出さない。
 */
export const DockingOnboardingWall: React.FunctionComponent<
  DockingOnboardingWallProps
> = ({
  registeredSlugs,
  onComplete,
  installPromptStore = pwaInstallPromptStore,
  isInstalledPwa = isRunningAsInstalledPwa,
  isMobileDisplay,
  requiresAdultConfirmation = false,
}: DockingOnboardingWallProps) => {
  const hasNonAdultBoard = registeredSlugs.some(
    (slug) => findMixLineupEntry(slug)?.isAdult === false,
  );
  const boardSettings = useMixStore((state) => state.boardSettings);
  const [isAdult, setIsAdult] = useState(false);
  const [step, setStep] = useState<OnboardingStep>(() =>
    hasNonAdultBoard ? "boards" : "age",
  );
  const [hasVisitedBoards, setHasVisitedBoards] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [installError, setInstallError] = useState(false);
  const installPromptState = useInstallPromptState(installPromptStore);
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement | HTMLParagraphElement>(null);
  const previousStepRef = useRef(step);
  const hasCompletedRef = useRef(false);
  const dialogRef = useDialogFocusTrap<HTMLElement>(true);
  const complete = useCallback((): void => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;
    onComplete(isAdult);
  }, [isAdult, onComplete]);

  useEffect(() => {
    if (previousStepRef.current === step) return;
    previousStepRef.current = step;
    titleRef.current?.focus({ preventScroll: true });
  }, [step]);

  useEffect(() => {
    if (step === "install" && installPromptState === "installed") {
      complete();
    }
  }, [complete, installPromptState, step]);

  useEffect(() => {
    if (step !== "install") return;
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.isComposing) return;
      event.preventDefault();
      complete();
    };
    document.addEventListener("keydown", handleEscape);
    return (): void => document.removeEventListener("keydown", handleEscape);
  }, [complete, step]);

  function finishOnboarding(): void {
    if (
      !isMobileDisplay ||
      isInstalledPwa() ||
      installPromptState === "installed"
    ) {
      complete();
      return;
    }
    setStep("install");
  }

  function finishBoardSelection(): void {
    setHasVisitedBoards(true);
    const includesAdultBoard = resolveEnabledMixSlugs(
      registeredSlugs,
      boardSettings,
    ).some((slug) => findMixLineupEntry(slug)?.isAdult === true);
    if ((includesAdultBoard || requiresAdultConfirmation) && !isAdult) {
      setStep("age");
      return;
    }
    finishOnboarding();
  }

  function finishAgeCheck(): void {
    if (hasVisitedBoards) {
      finishOnboarding();
      return;
    }
    setStep("boards");
  }

  async function requestInstall(): Promise<void> {
    setIsInstalling(true);
    setInstallError(false);
    try {
      const outcome = await installPromptStore.requestInstall();
      if (outcome === "unavailable") {
        setInstallError(true);
        return;
      }
      complete();
    } catch {
      setInstallError(true);
    } finally {
      setIsInstalling(false);
    }
  }

  return (
    <section
      ref={dialogRef}
      {...getDialogAccessibilityProps(titleId, true)}
      className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-background"
    >
      <article
        aria-labelledby={titleId}
        className="mx-auto flex min-h-full w-full max-w-2xl flex-col justify-center gap-4 px-4 py-8"
      >
        {/* 狭幅では折り返して横スクロールを出さない(4文字×60pxで320px級を超えるため) */}
        <p className="flex flex-wrap items-center justify-center gap-2.5 text-6xl font-bold text-foreground">
          <img
            src={mascot}
            alt=""
            aria-hidden="true"
            className="h-[5.625rem] w-[5.625rem]"
          />
          あいもげ
        </p>
        {step === "age" ? (
          <>
            <h1
              ref={titleRef as React.RefObject<HTMLHeadingElement>}
              id={titleId}
              tabIndex={-1}
              className="text-lg font-bold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              あなたは18歳以上ですか？
            </h1>
            <p className="text-sm text-muted-foreground">
              「はい」を選ぶと、R-18指定の板に入れるようになります。
            </p>
            <fieldset className="m-0 flex items-center justify-center gap-3 rounded-lg border-0 bg-card px-4 py-4">
              <legend className="sr-only">年齢確認</legend>
              <b className="text-sm font-bold text-foreground">いいえ</b>
              <Toggle
                checked={isAdult}
                onChange={setIsAdult}
                aria-label="18歳以上である"
              />
              <b className="text-sm font-bold text-foreground">はい</b>
            </fieldset>
            {isAdult ? (
              <Button variant="primary" onClick={finishAgeCheck}>
                次へ
              </Button>
            ) : hasVisitedBoards ? (
              <Button
                variant="secondary"
                onClick={(): void => setStep("boards")}
              >
                板選択に戻る
              </Button>
            ) : (
              <p role="status" className="text-sm text-destructive">
                現在R-18の板だけで構成されているため、ご利用いただけません
              </p>
            )}
          </>
        ) : step === "boards" ? (
          <>
            <h1
              ref={titleRef as React.RefObject<HTMLHeadingElement>}
              id={titleId}
              tabIndex={-1}
              className="text-lg font-bold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              表示項目を選んで下さい
            </h1>
            <p className="text-sm text-muted-foreground">
              選んだ板のスレを1つのカタログへ混ぜて表示します。
              <br />
              あとから設定で変更できます。
            </p>
            <BoardSelectionCards
              registeredSlugs={registeredSlugs}
              className="rounded-lg bg-card"
            />
            <Button variant="primary" onClick={finishBoardSelection}>
              次へ
            </Button>
          </>
        ) : (
          <div data-nosnippet className="flex flex-col gap-4">
            <p
              ref={titleRef as React.RefObject<HTMLParagraphElement>}
              id={titleId}
              tabIndex={-1}
              className="text-lg font-bold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              あいもげをアプリに追加しよう！
            </p>
            <p className="text-sm text-muted-foreground">
              ホーム画面から総合カタログをすぐに開けて、アプリのように使えます。
            </p>
            {installPromptState === "available" || isInstalling ? (
              <Button
                variant="primary"
                disabled={isInstalling}
                onClick={(): void => {
                  void requestInstall();
                }}
              >
                {isInstalling ? "追加画面を開いています…" : "アプリを追加"}
              </Button>
            ) : (
              <p className="rounded-lg bg-card px-4 py-3 text-sm text-foreground">
                追加できるブラウザでは、共有メニューまたはブラウザメニューから
                「ホーム画面に追加」「アプリをインストール」を選んでください。
              </p>
            )}
            <p
              role="status"
              aria-atomic="true"
              className="text-sm text-destructive"
            >
              {installError
                ? "追加画面を開けませんでした。ブラウザのメニューから追加してください。"
                : ""}
            </p>
            <Button variant="secondary" onClick={complete}>
              今はしない
            </Button>
          </div>
        )}
      </article>
    </section>
  );
};
